// incident-report-stepper.component.ts
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { IncidentReportStep1Component } from './incident-report-step1/incident-report-step1.component';
import { IncidentReportStep2Component } from './incident-report-step2/incident-report-step2.component';
import { StepperHeaderComponent } from '../../shared/stepper-header/stepper-header.component';

import { RouterModule } from '@angular/router';
import { StepsModule } from 'primeng/steps';
import { ButtonModule } from 'primeng/button';

import { HeroReport } from '../../model/hero-report/hero-report.model';
import { HeroReportService } from '../../model/hero-report/hero-report.service';
import { IncidentReportService } from '../../model/incident-report/incident-report.service';
import { HeroReportImageService } from '../../model/hero-report-image/hero-report-image.service';

import { ActivatedRoute } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';
import getDataUrlImageType from '../../utilities/getDataUrlImageType';

import { Router } from '@angular/router';

@Component({
  selector: 'app-incident-report-stepper',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IncidentReportStep1Component,
    IncidentReportStep2Component,
    StepperHeaderComponent,
    RouterModule,
    StepsModule,
    ButtonModule,
  ],
  templateUrl: './incident-report-stepper.component.html',
  styleUrls: ['./incident-report-stepper.component.scss'],
})
export class IncidentReportStepperComponent implements OnInit, OnDestroy {
  step = 0;
  stepValid = [false, false];

  // Step 1: fields eodIncidentNumber, incidentType
  initialForm!: FormGroup;
  // Step 2: narrative, disposition, completedDate, images[], widthCm, lengthCm, heightCm, diameterCm
  actionForm!: FormGroup;

  heroReport: HeroReport;

  private subs: Subscription[] = [];

  steps = [
    { label: $localize`:@@stepper.initialInfo:Initial Info` },
    { label: $localize`:@@stepper.actionByEod:Action by EOD` }
  ];

  constructor(private fb: FormBuilder, private heroReportService: HeroReportService, private incidentReportService: IncidentReportService, private heroReportImageService: HeroReportImageService, private route: ActivatedRoute, private router: Router) { }

  ngOnInit() {
    // from params
    const heroReportId = this.route.snapshot.paramMap.get('heroReportId');

    this.heroReportService.fetchById(heroReportId).then((heroReport) => {
      this.heroReport = heroReport;
    });

    this.initialForm = this.fb.group({
      eodIncidentNumber: [null],
      incidentType: [null, [Validators.required]],
    });

    this.actionForm = this.fb.group(
      {
        eodTeamDispatched: [''],
        departureDate: [null],
        arrivalDate: [null],
        narrative: [''],
        disposition: [null],
        completedDate: [null],
        images: [[]],
        widthCm: [null, [Validators.pattern(/^-?\d*\.?\d+$/)]],
        lengthCm: [null, [Validators.pattern(/^-?\d*\.?\d+$/)]],
        heightCm: [null, [Validators.pattern(/^-?\d*\.?\d+$/)]],
        diameterCm: [null, [Validators.pattern(/^-?\d*\.?\d+$/)]],
      },
      {
        validators: [
          (group: FormGroup) => {
            const departure = group.get('departureDate')?.value;
            const arrival = group.get('arrivalDate')?.value;
            const completed = group.get('completedDate')?.value;

            const errors: any = {};

            if (departure && arrival && new Date(arrival) <= new Date(departure)) {
              errors.arrivalBeforeDeparture = true;
            }
            if (arrival && completed && new Date(completed) <= new Date(arrival)) {
              errors.completedBeforeArrival = true;
            }

            return Object.keys(errors).length ? errors : null;
          }
        ]
      }
    );

    // track validity
    this.subs.push(
      this.initialForm.statusChanges.subscribe(s => this.stepValid[0] = s === 'VALID'),
      this.actionForm.statusChanges.subscribe(s => this.stepValid[1] = s === 'VALID'),
    );
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  nextStep() {
    const form = this.step === 0 ? this.initialForm : this.actionForm;
    if (form.valid) {
      this.step++;
    } else {
      form.markAllAsTouched();
    }
  }

  prevStep() {
    if (this.step > 0) this.step--;
  }

  async submit() {
    // mark both touched in case user never visited step 2
    this.initialForm.markAllAsTouched();
    this.actionForm.markAllAsTouched();

    if (!this.allStepsValid) return;

    const payload = {
      ...this.initialForm.value,
      ...this.actionForm.value,
      id: uuidv4(),
      heroReportId: this.heroReport.id,
    };


    await this.incidentReportService.create(payload);

    const eodImages = (this.actionForm.get('eodImages') as any)?.value || [];
    const heroImageCount = this.heroReport?.heroReportImages?.length || 0;

    for (let i = 0; i < eodImages.length; i++) {
      const img = eodImages[i];
      if (img.url && img.url.startsWith('data:')) {
        await this.heroReportImageService.createAndUpload({
          url: img.url,
          type: 'EOD',
          fileType: getDataUrlImageType(img.url),
          heroReportId: this.heroReport.id,
          displayOrder: heroImageCount + i,
          id: uuidv4(),
        });
      }
    }

    this.router.navigate(['/events']);
  }

  goBack() {
    this.router.navigate(['/events'], { queryParams: { recenter: 'true' } });
  }

  get allStepsValid() {
    return this.stepValid.every(v => v);
  }
}
